/* Elume: reveal on scroll, the hero light switch, buy-more tiers, drag-to-scroll strips. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('el-js');

  /* Reveal: elements marked [data-reveal] rise in once, staggered within their section.
     Content is visible by default; hiding only happens once this script runs. */
  function initReveal(root = document) {
    const items = root.querySelectorAll('[data-reveal]:not([data-revealed])');
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.setAttribute('data-revealed', ''));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const siblings = [...(el.closest('.shopify-section') || document).querySelectorAll('[data-reveal]:not([data-revealed])')];
          el.style.transitionDelay = `${Math.min(siblings.indexOf(el), 6) * 70}ms`;
          el.setAttribute('data-revealed', '');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((el) => io.observe(el));
  }

  /* The hero switch turns the hero field to night. It is the page's one live thing. */
  function initSwitch(root = document) {
    root.querySelectorAll('[data-el-switch]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      const hero = btn.closest('[data-el-hero]');
      const label = btn.querySelector('.el-switch__label');
      btn.addEventListener('click', () => {
        const on = btn.getAttribute('aria-pressed') !== 'true';
        btn.setAttribute('aria-pressed', String(on));
        hero?.classList.toggle('is-on', on);
        if (label) label.textContent = on ? label.dataset.on : label.dataset.off;
      });
    });
  }

  /* Hero kinds: pointing at a kind brings its photo up and points the button at it. Until the
     shopper does, the photos crossfade on their own; paused while the tab is hidden. */
  function initKinds(root = document) {
    root.querySelectorAll('[data-el-hero]').forEach((hero) => {
      if (hero.dataset.kinds) return;
      hero.dataset.kinds = '1';
      const kinds = [...hero.querySelectorAll('[data-el-kind]')];
      const slides = [...hero.querySelectorAll('[data-el-slide]')];
      const cta = hero.querySelector('[data-el-kind-cta]');
      if (kinds.length < 2) return;
      let current = 0, timer = null;
      const show = (i) => {
        current = i;
        kinds.forEach((k, n) => k.classList.toggle('is-active', n === i));
        slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
        if (cta) { cta.href = kinds[i].href; cta.textContent = kinds[i].dataset.label; }
      };
      const stop = () => { clearInterval(timer); timer = null; };
      const start = () => {
        if (reduce || timer) return;
        timer = setInterval(() => { if (!document.hidden) show((current + 1) % kinds.length); }, 5500);
      };
      kinds.forEach((k, i) => {
        k.addEventListener('pointerenter', () => { stop(); show(i); });
        k.addEventListener('focus', () => { stop(); show(i); });
      });
      start();
    });
  }

  /* Tabs (shop by room): arrow keys move between tabs; the chosen room's panel shows. */
  function initTabs(root = document) {
    root.querySelectorAll('[data-el-tabs]').forEach((box) => {
      if (box.dataset.bound) return;
      box.dataset.bound = '1';
      const tabs = [...box.querySelectorAll('[role="tab"]')];
      const panels = [...box.querySelectorAll('[data-el-panel]')];
      const select = (i, focus) => {
        tabs.forEach((t, n) => {
          const on = n === i;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          panels[n].hidden = !on;
          panels[n].classList.toggle('is-entering', on);
        });
        if (focus) tabs[i].focus();
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(i));
        t.addEventListener('keydown', (e) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
          if (!step) return;
          e.preventDefault();
          select((i + step + tabs.length) % tabs.length, true);
        });
      });
      panels.forEach((p, n) => { p.hidden = n !== 0; });
    });
  }

  /* Hero shutter: the lamp photo opens from the bottom edge once. */
  function initShutter(root = document) {
    root.querySelectorAll('[data-shutter]').forEach((el) => {
      if (reduce) return;
      requestAnimationFrame(() => el.classList.add('is-open'));
    });
  }

  /* Drag to scroll a strip with a mouse; touch and trackpads scroll natively. */
  function initDrag(root = document) {
    root.querySelectorAll('[data-el-drag]').forEach((row) => {
      if (row.dataset.bound) return;
      row.dataset.bound = '1';
      let startX = 0, startLeft = 0, moved = false, down = false;
      row.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; startX = e.clientX; startLeft = row.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        row.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => { down = false; });
      row.addEventListener('click', (e) => { if (moved) { e.preventDefault(); moved = false; } }, true);
    });
  }

  /* Buy more tiers: choosing a tier sets the product form's quantity, and the tier follows
     the quantity when the shopper changes it directly. */
  class ElBundle extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('product-information, .product-information, section')?.querySelector('quantity-selector-component');
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.setQuantity(Number(e.target.value));
      });
      this.onQty = (e) => this.sync(Number(e.detail?.quantity));
      document.addEventListener('quantity-selector:update', this.onQty);
      this.sync(this.currentQuantity());
    }
    disconnectedCallback() {
      document.removeEventListener('quantity-selector:update', this.onQty);
    }
    quantityInput() {
      return this.form?.querySelector('input[name="quantity"]');
    }
    currentQuantity() {
      return Number(this.quantityInput()?.value) || 1;
    }
    setQuantity(qty) {
      const input = this.quantityInput();
      if (!input) return;
      if (typeof this.form.setValue === 'function') this.form.setValue(qty);
      else input.value = qty;
      this.form.updateButtonStates?.();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync(qty) {
      if (!qty) return;
      const tier = Math.min(Math.max(qty, 1), 3);
      const radio = this.querySelector(`input[value="${tier}"]`);
      if (radio) radio.checked = true;
    }
  }
  if (!customElements.get('el-bundle')) customElements.define('el-bundle', ElBundle);

  function init(root) {
    initReveal(root);
    initSwitch(root);
    initShutter(root);
    initKinds(root);
    initTabs(root);
    initDrag(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
